'use client'

import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import Container from '@/components/structure/Container'
import Title from '@/components/content/common/Title'
import AppRanking, { AppRankingTitle } from '@/components/content/app/AppRanking'
import AwardIcon from '@/icons/brand/AwardIcon'
import ScrollView from '@/components/ui/ScrollView'
import { Placeholder } from '@/components/ui/Placeholder'
import Spliner from '@/lib/spline'
import { Icon } from '@/icons/Icon'
import { createLogger } from '@/lib/logger'
import { cn, isPopulatedObject } from '@/lib/utils'
import { queryPayload } from '@/lib/payloadClient'
import { easeMedium } from '@/lib/transitions'
import {
  orderAppsByRecommendation,
  fetchDealCountsForApps,
} from '@/components/content/best/helpers'
import type { HomepageBest } from '@/app/(app)/fetchers'
import type { App, AppCategoryRecommendation, Category } from '@/payload-types'

const logger = createLogger('HomeBest')

type HomeBestProps = {
  bests: HomepageBest[]
}

type BestDockProps = HomeBestProps & {
  selectedBest: number
  setSelectedBest: (id: number) => void
}

export default function HomeBest({ bests }: HomeBestProps) {
  const [selectedBest, setSelectedBest] = useState(bests[0].id)
  const [appCategoryRecommendations, setAppCategoryRecommendations] = useState<
    AppCategoryRecommendation[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [category, setCategory] = useState<Category | null>(null)
  const [dealCount, setDealCount] = useState(0)

  const { orderedAppRecs, secondaryApps } = orderAppsByRecommendation(appCategoryRecommendations)

  useEffect(() => {
    setAppCategoryRecommendations([])
    const best = bests.find((b) => b.id === selectedBest)
    if (!best) return
    const category = best.category as Category

    setCategory(category)
  }, [selectedBest, bests])

  useEffect(() => {
    async function getAppCategoryRecommendationsByCategoryId() {
      setIsLoading(true)
      setDealCount(0)

      try {
        const { docs: appCategoryRecommendations } = await queryPayload({
          collection: 'app_category_recommendation',
          select: {
            app: true,
            recommendation: true,
            category: true,
            order: true,
            classification: true,
            discontinuedAt: true,
          },
          populate: {
            app: {
              slug: true,
              websiteUrl: true,
              affiliateLink: true,
              partnershipTier: true,
              name: true,
              description: true,
              brandBackgroundColor: true,
              brandPrimaryColor: true,
              shortName: true,
              demoUrl: true,
              review: true,
              icon: true,
              glyph: true,
              categoryRecommendations: true,
              screenshots: true,
            },
            app_category_recommendation: {
              category: true,
            },
            category: {
              categoryCriteria: true,
              nameLabelSingular: true,
              nameLabelPlural: true,
            },
            category_criteria_criterion: {
              criterion: true,
            },
            criterion: {
              name: true,
              icon: true,
            },
            review: {},
            media: {
              alt: true,
              url: true,
              filename: true,
              coveragePercent: true,
              xOffsetPercent: true,
              yOffsetPercent: true,
            },
            comparison_apps_app: {
              comparison: true,
            },
            comparison: {},
          },
          where: { category: { equals: category?.id } },
          depth: 3,
          sort: ['order'],
          pagination: false,
          cacheKey: `query:acr:cat:${category?.id}`,
        })

        setAppCategoryRecommendations(appCategoryRecommendations)

        const { orderedAppRecs: rankedRecommendations } = orderAppsByRecommendation(appCategoryRecommendations)
        const appIds = rankedRecommendations
          .map((rec) => {
            const app = rec.app
            if (typeof app === 'number') return app
            if (isPopulatedObject<App>(app)) return app.id
            logger.warn('AppCategoryRecommendation has unpopulated app relation', {
              recId: rec.id,
            })
            return null
          })
          .filter((id): id is number => typeof id === 'number')

        if (appIds.length > 0) {
          const { dealCount } = await fetchDealCountsForApps(appIds)
          setDealCount(dealCount)
        }
      } catch (error) {
        logger.error('Error fetching best apps', error)
        setAppCategoryRecommendations([])
      } finally {
        setIsLoading(false)
      }
    }

    if (category) {
      getAppCategoryRecommendationsByCategoryId()
    }
  }, [category])

  const useSingular = false
  const nameLabel =
    (useSingular ? category?.nameLabelSingular : category?.nameLabelPlural) || 'Apps'

  const appRankingTitleTitle = !category
    ? `Best Apps of ${new Date().getFullYear()}`
    : `Best ${nameLabel} ${new Date().getFullYear()}`

  const appRankingTitleText = `${orderedAppRecs.length} app${orderedAppRecs.length === 1 ? '' : 's'}${dealCount > 0 ? ` and ${dealCount} deal${dealCount === 1 ? '' : 's'}` : ''}`

  return (
    <Container
      reduced={true}
      className="dark relative flex flex-col gap-16 overflow-hidden rounded-3xl bg-black pb-24"
      data-testid="home-best"
    >
      <div className="bg-gradient relative z-10 pt-10">
        <Spliner
          scene="/splines/best-logo.splinecode"
          fallback="/splines/best-logo-fallback.png"
          size={115}
          className="relative z-30 mb-3 mt-8 -translate-x-2"
          loadStrategy="after-interaction-idle"
          showFallbackOnDesktop
        />

        <Title
          title="Find the <span class='dark'>best</span> with efficient app"
          paragraphs={[
            'Discover the top apps in every category. Explore, compare, and choose what fits best.',
          ]}
          buttons={[
            { label: 'Explore top apps', link: '/best' },
            { label: 'Subscribe to updates', link: '/subscribe' },
          ]}
          type="primary"
          className="relative z-30 max-w-md"
        />

        <Image
          src="/img/home/laurel-glyph.svg"
          alt="App reviews"
          width={920}
          height={920}
          className="drag-none absolute left-1/2 top-0 z-20 -translate-x-1/2"
          style={{ height: 'auto' }}
        />
      </div>

      <BestDock bests={bests} selectedBest={selectedBest} setSelectedBest={setSelectedBest} />

      <AnimatePresence mode="wait">
        {orderedAppRecs.length > 0 &&
        category &&
        orderedAppRecs.every((rec) => rec.app) &&
        !isLoading ? (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={easeMedium}
          >
            <AppRanking
              apps={orderedAppRecs}
              otherApps={secondaryApps}
              category={category}
              theme="dark"
              title={
                <AppRankingTitle
                  title={appRankingTitleTitle}
                  icon={category.icon}
                  text={appRankingTitleText}
                  link={`/best/${category.slug}`}
                  theme="dark"
                />
              }
              className="max-w-content px-3 md:px-5"
              flag={['demo']}
            />
          </motion.div>
        ) : (
          <motion.div
            key={category?.id ?? 'skeleton'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={easeMedium}
            className="mx-auto flex w-full max-w-content flex-col gap-1 px-3 md:px-5"
            aria-busy={isLoading}
            aria-label={isLoading ? 'Loading apps' : 'No apps available'}
          >
            {Array.from({ length: 7 }).map((_, index) => (
              <Placeholder key={index} className={index === 0 ? 'h-[50px]' : 'h-20'} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mx-auto flex gap-2">
        <AwardIcon className="h-8 w-auto fill-white" />
        <AwardIcon className="h-8 w-auto -scale-x-100 fill-white" />
      </div>
    </Container>
  )
}

export function BestDock({ bests, selectedBest, setSelectedBest }: BestDockProps) {
  return (
    <ScrollView className="mx-auto flex gap-5 px-3 md:justify-center">
      {bests.map((best) => {
        const isSelected = best.id === selectedBest
        return (
          <button
            key={best.id}
            onClick={() => setSelectedBest(best.id)}
            className={cn(
              'relative flex w-fit shrink-0 cursor-pointer items-center gap-2.5 rounded-full px-3.5 transition-colors duration-500',
              isSelected ? 'text-primary-500' : 'text-white',
            )}
          >
            <Icon name="best" />
            <span className="text-14">Best of {best.name}</span>
            {isSelected && (
              <motion.div
                layoutId="best-highlight"
                className="absolute inset-0 rounded-full border border-primary-500/15 bg-primary-500/10"
              />
            )}
          </button>
        )
      })}
    </ScrollView>
  )
}
